import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Controller } from "react-hook-form";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
} from "@mui/material";

import Input from "../../../../../components/inputs/Input";
import {
  useGetManufacturers,
  useGetModels,
  useGetPolicesDevicesBySponsor,
} from "../../../../../services/microservice/micelu/device/use-device";
import {
  IModel,
  IPolicies,
} from "../../../../../services/microservice/micelu/device/device-service.interface";
import capitalizeFirstLetter from "../../../../../util/toCapitalize";
import { formatValue } from "react-currency-input-field";
import DropdownFilter from "../../../../../components/inputs/DropdownFilter";

const DeviceComponent = ({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
  setSelectedPlan,
}) => {
  const [models, setModels] = useState<IModel[]>([]);
  const [error, setError] = useState<string>("");
  const { mutateAsync: getPolicies, isLoading: loadingPolicies } =
    useGetPolicesDevicesBySponsor();
  const [infoPolicies, setInfoPolicies] = useState<IPolicies[] | null>(null);
  const { data: Manufacturers } = useGetManufacturers("TOPTRADING");
  const { mutateAsync: getModels } = useGetModels("TOPTRADING");

  const getErrorMessage = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (
      value &&
      typeof value === "object" &&
      "message" in (value as { message?: unknown })
    ) {
      return getErrorMessage((value as { message?: unknown }).message);
    }
    return "";
  };

  useEffect(() => {
    if (watch("manufacturer") || watch("imei") || watch("insuredValue")) {
      setValue("model", "");
      clearErrors("plan");
      setInfoPolicies(null);
      const manufacturer = watch("manufacturer")?.id_system;
      getModels(manufacturer).then((res) => setModels(res.data.models));
    } else {
      setInfoPolicies(null);
      setValue("model", "");
      setModels([]);
    }
  }, [watch("manufacturer"), watch("imei"), watch("insuredValue")]);

  const getPlansDevices = async (values) => {
    try {
      const res = await getPolicies({
        imei: values.imei,
        tac: values.model.reference_tac,
        insuredValue: values.insuredValue?.replace(/,/g, "").replace("$", ""),
        manufacturer: values.manufacturer.nombre,
        model: values.model.device_name,
        sponsorID: "TOPTRADING",
      });
      if (res) {
        setValue("plan", "" as any);
        setValue("valuePolicie", "");
        setInfoPolicies(
          res.data.policies.filter((d) => d.name.includes("uevos")),
        );
        setError("");
      }
    } catch (error) {
      if (error.status === 400) {
        setError(
          getErrorMessage(error?.response?.data?.error?.message) ||
            "Ocurrio un error inesperado",
        );
      } else {
        setError("Ocurrio un error inesperado");
      }
    }
  };

  useEffect(() => {
    if (watch("plan")) {
      const plan = watch("plan") as any;
      if (!_.isEmpty(plan)) {
        const currentPlan = infoPolicies.filter((pol) =>
          pol.pricingOptions.some((opt) => opt.id === Number(plan)),
        )[0];
        const selectedOption = currentPlan.pricingOptions.filter(
          (opt) => opt.id === Number(plan),
        )[0];
        setValue(
          "valuePolicie",
          `$ ${selectedOption?.paymentAmountWithTaxes
            .toString()
            .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")}`,
        );
      }
    }
  }, [watch("plan")]);

  useEffect(() => {
    if (
      watch("insuredValue") !== "" &&
      watch("manufacturer") !== null &&
      watch("model") !== null &&
      watch("model") !== "" &&
      watch("insuredValue") !== undefined
    ) {
      getPlansDevices({
        imei: "",
        model: watch("model"),
        manufacturer: watch("manufacturer"),
        insuredValue: watch("insuredValue"),
      });
    } else {
      setValue("plan", "" as any);
      setValue("valuePolicie", "");
      clearErrors("plan");
      setInfoPolicies(null);
    }
  }, [watch("model"), watch("insuredValue"), watch("manufacturer")]);

  useEffect(() => {
    if (watch("insuredValue") === "$") {
      setValue("insuredValue", "");
    }
  }, [watch("insuredValue")]);

  const displayCurrency = (val: string) => {
    let formatCurrency = "";
    const currency = val?.replace(/,/g, "").replace("$", "");
    if (val !== "" && val !== "$Na" && val !== "$NaN" && val !== "$") {
      formatCurrency = formatValue({
        value: currency,
        groupSeparator: ",",
        decimalSeparator: ".",
        prefix: "$",
      });
    } else {
      return "";
    }
    return formatCurrency;
  };

  return (
    <section className="w-full">
      <div className="mb-10 w-full">
        <div className="space-y-6">
          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos dispositivo</h2>
            <div className="grid grid-cols-2 gap-6">
              <Controller
                name="imei"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="IMEI"
                    required
                    error={errors?.imei?.message}
                  />
                )}
              />
              <Controller
                name="insuredValue"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="Valor cubierto"
                    required
                    error={errors?.insuredValue?.message}
                    value={displayCurrency(watch("insuredValue"))}
                  />
                )}
              />
              <Controller
                name="manufacturer"
                control={control}
                render={({ field }) => (
                  <DropdownFilter
                    {...field}
                    options={Manufacturers?.data?.manufacturers || []}
                    placeholder="Selecciona la marca"
                    label="nombre"
                    city={false}
                    required
                    error={errors?.manufacturer?.id_system?.message}
                  />
                )}
              />
              <Controller
                name="model"
                control={control}
                render={({ field }) => (
                  <DropdownFilter
                    {...field}
                    options={models || []}
                    placeholder="Selecciona el modelo"
                    label="device_name"
                    city={false}
                    required
                    error={errors?.model?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos plan</h2>
            <Controller
              name="plan"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" className="mb-10">
                  <p className="my-0 py-0 text-base">
                    Seleccione plan{" "}
                    <span className="text-center text-3xl text-critical">
                      *
                    </span>
                    {errors?.plan && (
                      <span className="my-0 py-0 text-base text-critical">
                        {getErrorMessage(errors?.plan?.message)}
                      </span>
                    )}
                  </p>
                  {loadingPolicies ? (
                    <Stack>
                      {[...Array(infoPolicies?.length || 2)].map((_, i) => (
                        <Stack
                          key={i}
                          sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Skeleton variant="circular" width={20} height={20} />
                          <Skeleton
                            variant="text"
                            width="80%"
                            sx={{ marginLeft: "12px" }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  ) : infoPolicies?.length > 0 ? (
                    <RadioGroup
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="mb-3"
                    >
                      {infoPolicies.map((policy, idx) =>
                        policy.pricingOptions.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            value={option.id}
                            control={<Radio />}
                            onChange={() => setSelectedPlan(policy.id)}
                            label={
                              policy.name
                                ? `${policy?.name} - ${capitalizeFirstLetter(option?.paymentFrequency)}`
                                : "Sin nombre"
                            }
                          />
                        )),
                      )}
                    </RadioGroup>
                  ) : (
                    <p className="my-0 mb-10 py-0 text-base">
                      No hay planes disponibles
                    </p>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="valuePolicie"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  classNameContainer="w-full mb-7"
                  classNameInput="w-full"
                  placeholder="Valor cobertura IVA incluido"
                  readOnly
                  error={errors?.valuePolicie?.message}
                />
              )}
            />

            {error && (
              <p className="text-md text-critical">{getErrorMessage(error)}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeviceComponent;
